import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { AuthState, AuthUser } from '@/types/auth';
import { Provider } from '@/types/tenant';
import { computeProviderDomain, setProviderDomain, setProviderSlug } from '@/lib/provider-context';
import { getDevProviderSlug, getCurrentDomain } from '@/utils/domain';
import { coerceAuthUser, coerceProvider } from '@/utils/auth-session';
import { logger } from '@/lib/logger';

interface AuthContextType extends AuthState {
  checkAuth: () => Promise<void>;
  login: (user: AuthUser, provider: Provider, token: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    provider: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const checkAuth = useCallback(async () => {
    if (isCheckingAuth) {
      logger.debug('🔍 Auth check already in progress, skipping');
      return;
    }
    
    logger.debug('🔍 checkAuth called, current auth state:', authState.isAuthenticated);
    setIsCheckingAuth(true);
    const token = authService.getAuthToken();
    
    if (!token) {
      logger.debug('🔍 No token found, setting unauthenticated state');
      setAuthState({
        user: null,
        provider: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      setIsCheckingAuth(false);
      return;
    }

    // Check if we have a mock token but are in live mode
    const isMockToken = token.startsWith('mock_');
    const isUsingMockData = authService.isUsingMockData();
    
    if (isMockToken && !isUsingMockData) {
      logger.info('🔄 Clearing mock token in live mode');
      authService.clearAuthToken();
      setAuthState({
        user: null,
        provider: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Mock token invalid in live mode',
      });
      setIsCheckingAuth(false);
      return;
    }

    try {
      logger.debug('🔐 Checking authentication...', { token: `${token.substring(0, 20)}...` });
      const response = await authService.getCurrentProviderUser();
      logger.debug('📋 Full response object:', response);
      logger.debug('📋 Response.data:', response.data);
      
      // Handle both wrapped and direct response structures
      const rawUser = response.data?.user ?? (response as Record<string, unknown>).user;
      const rawProvider = response.data?.provider ?? (response as Record<string, unknown>).provider;

      const userData = coerceAuthUser(rawUser);
      const providerData = coerceProvider(rawProvider);

      if (!userData) {
        throw new Error('No user data found in response');
      }
      
      logger.info('✅ Authentication verified:', userData.email);
      
      setAuthState({
        user: userData,
        provider: providerData,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      const resolvedDomain = computeProviderDomain({
        provider: providerData,
        fallbackSlug: providerData?.slug ?? getDevProviderSlug(),
        fallbackHostname: getCurrentDomain().hostname,
      });
      setProviderDomain(resolvedDomain);
      const resolvedSlug = providerData?.slug ?? getDevProviderSlug();
      setProviderSlug(resolvedSlug);
      logger.debug('🔍 Auth check completed successfully');
    } catch (error: unknown) {
      logger.error('❌ Authentication verification failed:', error);
      
      // Only clear auth on 401 errors (invalid/expired token)
      // Don't log out on parsing errors or network issues
      if (error && typeof error === 'object' && 'response' in error && 
          (error.response as { status?: number })?.status === 401) {
        logger.warn('🔓 Token invalid/expired, clearing auth');
        authService.clearAuthToken();
        setAuthState({
          user: null,
          provider: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Authentication expired',
        });
      } else {
        logger.warn('⚠️ Non-auth error, keeping user logged in');
        // Keep the user logged in but show error state
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Failed to load user data',
        }));
      }
    }
    
    setIsCheckingAuth(false);
  }, [isCheckingAuth, authState.isAuthenticated]);

  // Check authentication status on mount and periodically
  useEffect(() => {
    if (!hasInitialized) {
      checkAuth().finally(() => setHasInitialized(true));
      
      // Optional: Add periodic check every 30 minutes
      const interval = setInterval(() => {
        logger.debug('🔄 Periodic auth check...');
        checkAuth();
      }, 30 * 60 * 1000); // 30 minutes
      
      return () => clearInterval(interval);
    }
  }, [hasInitialized, checkAuth]);

  const login = useCallback(async (user: AuthUser, provider: Provider, token: string) => {
    setAuthState({
      user,
      provider,
      token,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
    
    // Apply provider branding after authentication
    applyProviderBranding(provider);
    const resolvedDomain = computeProviderDomain({
      provider,
      fallbackSlug: provider?.slug ?? getDevProviderSlug(),
      fallbackHostname: getCurrentDomain().hostname,
    });
    setProviderDomain(resolvedDomain);
    const resolvedSlug = provider?.slug ?? getDevProviderSlug();
    setProviderSlug(resolvedSlug);
  }, []);

  // Apply provider branding
  const applyProviderBranding = (provider: Provider) => {
    // This would apply branding if we had it in the provider object
    // For now, we'll keep default Florentin branding
    // Future: Add branding field to Provider type and apply here
    void provider;
  };

  const logout = useCallback(async () => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authService.logoutProvider();
    } catch (error) {
      // Even if logout fails, clear local state
      logger.error('Logout error:', error);
    }
    
    setAuthState({
      user: null,
      provider: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    
    navigate('/provider/login');
  }, [navigate]);

  const contextValue: AuthContextType = {
    ...authState,
    checkAuth,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook to require authentication
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useRequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/provider/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  return { isAuthenticated, isLoading };
}
